import { useEffect } from "react";

/* ============================================================
   Fluid font-scale walker.
   ------------------------------------------------------------
   The mockup uses inline `style={{ fontSize: 14 }}` on ~40
   elements per page (pixel-perfect small-phone layout). We
   preserve those values as the SMALL-END anchor and scale them
   UP continuously as the viewport widens.

   Scale = smooth linear function of viewport CSS width:
     - w ≤ 430 (iPhone Pro Max and narrower): scale = 1.0 (untouched)
     - w = 720 (folded fold): scale ≈ 1.21
     - w = 830 (unfolded fold): scale ≈ 1.29
     - w = 1024 (iPad): scale ≈ 1.43
     - w = 1280 (small desktop): scale ≈ 1.62
     - w = 1440 (desktop): scale ≈ 1.74
     - w ≥ 1600 (wide desktop): scale = 1.85 (capped)

   No stepped breakpoints. Resizing the browser from 430→1600 gives
   a smooth continuous font size change, not a series of jumps at
   768/1024/1280.

   Applied by walking every inline-style font-size in the DOM,
   capturing the original px value on `data-orig-fs`, and rewriting
   `el.style.fontSize` to `orig * scale`. Line-heights scale
   proportionally when present so text doesn't cramp.

   Runs on: mount, resize, orientationchange, and DOM mutation
   (React re-renders reset inline styles).
   ============================================================ */

const ANCHOR_MIN_W = 430;   // scale = 1.0 at or below this width
const ANCHOR_MAX_W = 1600;  // scale hits max at this width
const SCALE_MAX    = 1.85;  // ~85% larger at wide desktop

/** Continuous linear scale between the two anchors.
 *  Below ANCHOR_MIN_W → 1.0 (mobile untouched)
 *  Above ANCHOR_MAX_W → SCALE_MAX (capped)
 *  In between → linear interpolation. */
function scaleFor(w) {
  if (w <= ANCHOR_MIN_W) return 1.0;
  if (w >= ANCHOR_MAX_W) return SCALE_MAX;
  const t = (w - ANCHOR_MIN_W) / (ANCHOR_MAX_W - ANCHOR_MIN_W);
  return 1.0 + t * (SCALE_MAX - 1.0);
}

function applyScale() {
  const w = window.innerWidth;
  const s = scaleFor(w);
  const nodes = document.querySelectorAll('[style*="font-size"]');
  for (const el of nodes) {
    // Capture original px value the first time we see this element.
    // Read from the style ATTRIBUTE (not computed style) so we get
    // React's original value, not any prior scale we applied.
    if (!el.dataset.origFs) {
      const raw = el.getAttribute("style") || "";
      const m = raw.match(/font-size\s*:\s*(\d+(?:\.\d+)?)px/i);
      if (!m) continue;
      el.dataset.origFs = m[1];
      const lm = raw.match(/line-height\s*:\s*(\d+(?:\.\d+)?)/i);
      if (lm) el.dataset.origLh = lm[1];
    }
    const orig = parseFloat(el.dataset.origFs);
    if (!Number.isFinite(orig)) continue;
    const target = Math.round(orig * s * 10) / 10;
    const cur = parseFloat(el.style.fontSize);
    if (Math.abs(cur - target) > 0.5) {
      el.style.fontSize = target + "px";
    }
    if (el.dataset.origLh) {
      const origLh = parseFloat(el.dataset.origLh);
      const targetLh = Math.round(origLh * s * 100) / 100;
      if (Math.abs(parseFloat(el.style.lineHeight) - targetLh) > 0.05) {
        el.style.lineHeight = targetLh + "";
      }
    }
  }
}

export function useFontScale() {
  useEffect(() => {
    applyScale();
    const onResize = () => applyScale();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        applyScale();
      });
    };
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, {
      subtree:          true,
      childList:        true,
      attributes:       true,
      attributeFilter: ["style"],
    });

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      mo.disconnect();
    };
  }, []);
}
